import { getStytchRedirectURL } from '@/utilities/getDomain'
import canUseDOM from '@/utilities/canUseDOM'
import { getCurrentEnvironment } from '../getEnvironment'
import { SHARED_PATHS } from '@/utilities/paths'

export type PortalType = 'client' | 'patient'
export const PORTAL = { CLIENT: 'client', PATIENT: 'patient' } as const

type InferArgs = {
  host?: string | null
  pathname?: string | null
  search?: string | null
}

// Pure inference that can be used in any environment (SSR/Edge/Client).
export const inferPortalType = ({ host, pathname, search }: InferArgs = {}): PortalType => {
  const normalizedHost = (host || '').toLowerCase()
  const normalizedPathname = (pathname || '').toLowerCase()
  const normalizedSearch = (search || '').toLowerCase()

  // Client host indicates client portal.
  if (/^client\./.test(normalizedHost)) return PORTAL.CLIENT

  // Path-based detection for client portal.
  if (normalizedPathname.startsWith('/client')) return PORTAL.CLIENT

  const searchParams = new URLSearchParams(
    normalizedSearch.startsWith('?') ? normalizedSearch.slice(1) : normalizedSearch,
  )

  // Shared /login can be client portal when slug=client (e.g. after password reset redirect).
  if (normalizedPathname === SHARED_PATHS.login && searchParams.get('slug') === 'client') {
    return PORTAL.CLIENT
  }

  // Authentication route may carry a slug that indicates portal type.
  if (normalizedPathname.startsWith(SHARED_PATHS.authenticate)) {
    if (searchParams.get('slug') === 'client') return PORTAL.CLIENT
  }

  return PORTAL.PATIENT
}

export const getPortalType = (): PortalType => {
  // If canUseDOM is truthy, use window; otherwise, default to PATIENT.
  if (!canUseDOM) return PORTAL.PATIENT

  return inferPortalType({
    host: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
  })
}

// Ergonomic helper to check if current portal is client portal.
export const isClientPortal = (): boolean => getPortalType() === PORTAL.CLIENT

export const getSharedRedirectURL = (): string => getStytchRedirectURL()

// Env-derived constants
const ENV = getCurrentEnvironment()
export const STYTCH_ACTIVE_SESSION_DURATION = ENV === 'development' ? 1440 : 60
